import { Business } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { useApp } from '@/lib/context';
import {
  useApplyRefresh,
  useFetchRefreshPreview,
  useMapReferenceInfo,
  usePreviewMapReferenceUpdate,
  useUpdateMapReference,
  type CachedMapResponse,
  type RefreshComparison,
  type UpdateMapReferencePreview,
} from '@/pages/businesses/hooks/useBusinessRefresh';
import {
  mdiAlertCircle,
  mdiAlertOutline,
  mdiCheck,
  mdiCheckboxBlankOutline,
  mdiCheckboxMarked,
  mdiClockOutline,
  mdiContentCopy,
  mdiImage,
  mdiMagnify,
  mdiMapMarker,
  mdiPencil,
  mdiRefresh,
} from '@mdi/js';
import * as React from 'react';

interface BusinessMapProvidersTabProps {
  business: Business;
}

interface OpeningHoursEntry {
  day: number;
  open: string;
  close: string;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const BusinessMapProvidersTab: React.FC<
  BusinessMapProvidersTabProps
> = ({ business }) => {
  const { currentUser } = useApp();
  const isPlatformAdmin = currentUser?.roles?.includes('platform_admin');

  // Map reference info
  const { data: mapRefInfo, isLoading: isLoadingMapRef } = useMapReferenceInfo(
    business.id,
  );

  // Update place ID state
  const [isEditingPlaceId, setIsEditingPlaceId] = React.useState(false);
  const [newPlaceId, setNewPlaceId] = React.useState('');
  const [preview, setPreview] =
    React.useState<UpdateMapReferencePreview | null>(null);
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  // Update place ID mutations
  const previewMutation = usePreviewMapReferenceUpdate();
  const updateMutation = useUpdateMapReference();

  // Sync state
  const [selectedChanges, setSelectedChanges] = React.useState<Set<string>>(
    new Set(),
  );
  const [comparison, setComparison] = React.useState<RefreshComparison | null>(
    null,
  );
  const [cachedResponse, setCachedResponse] =
    React.useState<CachedMapResponse | null>(null);
  const [lastAppliedAt, setLastAppliedAt] = React.useState<string | null>(null);
  const [appliedChangesCount, setAppliedChangesCount] = React.useState(0);
  const [lastProviderName, setLastProviderName] = React.useState<string | null>(
    null,
  );

  const fetchPreviewMutation = useFetchRefreshPreview();
  const applyRefreshMutation = useApplyRefresh();

  const providerName =
    comparison?.provider?.name || mapRefInfo?.provider?.name || 'Map Provider';

  // Reset edit state when closing
  const handleCancelEdit = () => {
    setIsEditingPlaceId(false);
    setNewPlaceId('');
    setPreview(null);
    setShowConfirmation(false);
  };

  // Preview the new place ID
  const handlePreviewPlaceId = async () => {
    if (!business.id || !newPlaceId.trim()) return;

    const result = await previewMutation.mutateAsync({
      businessId: business.id,
      newPlaceId: newPlaceId.trim(),
    });
    setPreview(result);
  };

  // Confirm and apply the update
  const handleConfirmUpdate = async () => {
    if (!business.id || !newPlaceId.trim()) return;

    await updateMutation.mutateAsync({
      businessId: business.id,
      newPlaceId: newPlaceId.trim(),
    });

    handleCancelEdit();
  };

  // Sync handlers
  const handleCheckForUpdates = async () => {
    if (!business.id) return;

    setLastAppliedAt(null);
    setAppliedChangesCount(0);
    const result = await fetchPreviewMutation.mutateAsync(business.id);
    setComparison(result);
    if (result.cachedResponse) {
      setCachedResponse(result.cachedResponse);
    }
    setSelectedChanges(new Set());
  };

  const toggleChange = (key: string) => {
    setSelectedChanges(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleApplyChanges = async () => {
    if (!business.id) return;

    const changesCount = selectedChanges.size;
    const currentProviderName = providerName;

    await applyRefreshMutation.mutateAsync({
      businessId: business.id,
      providerName: currentProviderName,
      applyName: selectedChanges.has('name'),
      applyAddress: selectedChanges.has('address'),
      applyPhone: selectedChanges.has('phone'),
      applyWebsite: selectedChanges.has('website'),
      applyOpeningHours: selectedChanges.has('openingHours'),
      addNewPhotos: selectedChanges.has('photos'),
      cachedResponse: cachedResponse ?? undefined,
    });

    setSelectedChanges(new Set());
    setComparison(null);
    setCachedResponse(null);
    setLastAppliedAt(new Date().toISOString());
    setAppliedChangesCount(changesCount);
    setLastProviderName(currentProviderName);
  };

  const isLoading = fetchPreviewMutation.isPending;
  const error = fetchPreviewMutation.error;

  // Render helpers for sync section
  const renderChangeItem = (
    key: string,
    label: string,
    oldValue: string | number | undefined,
    newValue: string | number | undefined,
  ) => {
    const isSelected = selectedChanges.has(key);
    return (
      <div
        key={key}
        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
          isSelected
            ? 'border-coffice-blue bg-coffice-blue/10'
            : 'border-grey-200 hover:border-grey-300'
        }`}
        onClick={() => toggleChange(key)}
      >
        <div className='flex items-start space-x-3'>
          <Icon
            path={isSelected ? mdiCheckboxMarked : mdiCheckboxBlankOutline}
            size={20}
            className={isSelected ? 'text-coffice-blue' : 'text-grey-400'}
          />
          <div className='flex-1 min-w-0'>
            <p className='font-medium text-grey-900'>{label}</p>
            <div className='mt-2 space-y-1'>
              <div className='flex items-start'>
                <span className='text-xs text-grey-500 w-12 flex-shrink-0'>
                  Old:
                </span>
                <span className='text-sm text-red-600 line-through break-all'>
                  {oldValue || '(empty)'}
                </span>
              </div>
              <div className='flex items-start'>
                <span className='text-xs text-grey-500 w-12 flex-shrink-0'>
                  New:
                </span>
                <span className='text-sm text-green-600 break-all'>
                  {newValue || '(empty)'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderOpeningHoursChange = (comp: RefreshComparison) => {
    if (!comp.openingHoursChange) return null;

    const isSelected = selectedChanges.has('openingHours');
    const oldHours = (comp.openingHoursChange.old || []) as OpeningHoursEntry[];
    const newHours = (comp.openingHoursChange.new || []) as OpeningHoursEntry[];

    return (
      <div
        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
          isSelected
            ? 'border-coffice-blue bg-coffice-blue/10'
            : 'border-grey-200 hover:border-grey-300'
        }`}
        onClick={() => toggleChange('openingHours')}
      >
        <div className='flex items-start space-x-3'>
          <Icon
            path={isSelected ? mdiCheckboxMarked : mdiCheckboxBlankOutline}
            size={20}
            className={isSelected ? 'text-coffice-blue' : 'text-grey-400'}
          />
          <div className='flex-1'>
            <div className='flex items-center space-x-2'>
              <Icon
                path={mdiClockOutline}
                size={16}
                className='text-grey-500'
              />
              <p className='font-medium text-grey-900'>Opening Hours</p>
            </div>
            <div className='mt-3 grid grid-cols-2 gap-4'>
              <div>
                <p className='text-xs text-grey-500 mb-2'>Current Hours</p>
                {oldHours.length === 0 ? (
                  <p className='text-sm text-grey-400'>No hours set</p>
                ) : (
                  <div className='space-y-1'>
                    {oldHours.map((h, i) => (
                      <p key={i} className='text-xs text-grey-600'>
                        {DAY_NAMES[h.day]}: {h.open} - {h.close}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className='text-xs text-grey-500 mb-2'>
                  {providerName} Hours
                </p>
                {newHours.length === 0 ? (
                  <p className='text-sm text-grey-400'>No hours found</p>
                ) : (
                  <div className='space-y-1'>
                    {newHours.map((h, i) => (
                      <p key={i} className='text-xs text-green-600'>
                        {DAY_NAMES[h.day]}: {h.open} - {h.close}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPhotosChange = (comp: RefreshComparison) => {
    if (comp.newPhotosCount === 0) return null;

    const isSelected = selectedChanges.has('photos');

    return (
      <div
        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
          isSelected
            ? 'border-coffice-blue bg-coffice-blue/10'
            : 'border-grey-200 hover:border-grey-300'
        }`}
        onClick={() => toggleChange('photos')}
      >
        <div className='flex items-start space-x-3'>
          <Icon
            path={isSelected ? mdiCheckboxMarked : mdiCheckboxBlankOutline}
            size={20}
            className={isSelected ? 'text-coffice-blue' : 'text-grey-400'}
          />
          <div className='flex-1'>
            <div className='flex items-center space-x-2'>
              <Icon path={mdiImage} size={16} className='text-grey-500' />
              <p className='font-medium text-grey-900'>New Photos Available</p>
            </div>
            <p className='text-sm text-green-600 mt-1'>
              {comp.newPhotosCount} new photo
              {comp.newPhotosCount > 1 ? 's' : ''} from {providerName}
            </p>
            <p className='text-xs text-grey-500 mt-1'>
              Photos will be added without replacing existing ones
            </p>
          </div>
        </div>
      </div>
    );
  };

  const hasAnyChanges =
    comparison &&
    (comparison.hasChanges ||
      comparison.nameChange ||
      comparison.addressChange ||
      comparison.phoneChange ||
      comparison.websiteChange ||
      comparison.openingHoursChange ||
      comparison.newPhotosCount > 0);

  return (
    <div className='p-6 space-y-8'>
      {/* Map Provider Info Section */}
      <div className='space-y-4'>
        <div className='flex items-center space-x-2'>
          <Icon path={mdiMapMarker} size={24} className='text-coffice-blue' />
          <h3 className='text-lg font-medium text-grey-900'>Map Provider</h3>
        </div>

        {isLoadingMapRef ? (
          <div className='bg-grey-50 rounded-lg p-4'>
            <div className='animate-pulse flex space-x-4'>
              <div className='flex-1 space-y-2'>
                <div className='h-4 bg-grey-200 rounded w-1/4'></div>
                <div className='h-4 bg-grey-200 rounded w-1/2'></div>
              </div>
            </div>
          </div>
        ) : mapRefInfo ? (
          <div className='bg-grey-50 rounded-lg p-4 space-y-3'>
            <div className='flex justify-between items-start'>
              <div>
                <p className='text-xs text-grey-500'>Provider</p>
                <p className='text-sm font-medium text-grey-900'>
                  {mapRefInfo.provider.name}
                </p>
              </div>
              {mapRefInfo.lastSyncedAt && (
                <div className='text-right'>
                  <p className='text-xs text-grey-500'>Last Synced</p>
                  <p className='text-sm text-grey-700'>
                    {new Date(mapRefInfo.lastSyncedAt).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            <div>
              <p className='text-xs text-grey-500'>Place ID</p>
              <div className='flex items-center space-x-2'>
                <code className='text-sm font-mono text-grey-800 bg-grey-100 px-2 py-1 rounded break-all'>
                  {mapRefInfo.externalId}
                </code>
                <button
                  onClick={() =>
                    navigator.clipboard.writeText(mapRefInfo.externalId)
                  }
                  className='p-1 text-grey-400 hover:text-grey-600'
                  title='Copy to clipboard'
                >
                  <Icon path={mdiContentCopy} size={16} />
                </button>
              </div>
            </div>

            {/* Update Place ID (Platform Admin Only) */}
            {isPlatformAdmin && !isEditingPlaceId && (
              <Button
                label='Update Place ID'
                icon={mdiPencil}
                color='secondary'
                small
                onClick={() => setIsEditingPlaceId(true)}
              />
            )}

            {/* Edit Place ID Form */}
            {isPlatformAdmin && isEditingPlaceId && !preview && (
              <div className='border-t border-grey-200 pt-4 mt-4 space-y-4'>
                <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-3'>
                  <div className='flex items-start space-x-2'>
                    <Icon
                      path={mdiAlertOutline}
                      size={20}
                      className='text-yellow-600 flex-shrink-0'
                    />
                    <p className='text-sm text-yellow-800'>
                      Changing the Place ID will link this business to a
                      different location in the map provider. Use this to
                      correct a wrongly imported business.
                    </p>
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-grey-700 mb-1'>
                    New Place ID
                  </label>
                  <input
                    type='text'
                    value={newPlaceId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setNewPlaceId(e.target.value)
                    }
                    placeholder='Enter the new Google Place ID'
                    className='w-full px-3 py-2 border border-grey-300 rounded-md shadow-sm focus:outline-none focus:ring-coffice-blue focus:border-coffice-blue text-sm'
                  />
                </div>

                <div className='flex space-x-2'>
                  <Button
                    label='Preview'
                    icon={mdiMagnify}
                    color='primary'
                    small
                    onClick={handlePreviewPlaceId}
                    disabled={!newPlaceId.trim() || previewMutation.isPending}
                    loading={previewMutation.isPending}
                  />
                  <Button
                    label='Cancel'
                    color='secondary'
                    small
                    onClick={handleCancelEdit}
                  />
                </div>
              </div>
            )}

            {/* Preview Result */}
            {isPlatformAdmin && preview && !showConfirmation && (
              <div className='border-t border-grey-200 pt-4 mt-4 space-y-4'>
                <h4 className='font-medium text-grey-900'>
                  Preview: New Place Details
                </h4>
                <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2'>
                  <div>
                    <p className='text-xs text-grey-500'>Name</p>
                    <p className='text-sm font-medium text-grey-900'>
                      {preview.newPlaceData.name}
                    </p>
                  </div>
                  <div>
                    <p className='text-xs text-grey-500'>Address</p>
                    <p className='text-sm text-grey-700'>
                      {preview.newPlaceData.address}
                    </p>
                  </div>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <p className='text-xs text-grey-500'>City</p>
                      <p className='text-sm text-grey-700'>
                        {preview.newPlaceData.city}
                      </p>
                    </div>
                    <div>
                      <p className='text-xs text-grey-500'>Country</p>
                      <p className='text-sm text-grey-700'>
                        {preview.newPlaceData.country}
                      </p>
                    </div>
                  </div>
                  {preview.newPlaceData.photoCount !== undefined && (
                    <div>
                      <p className='text-xs text-grey-500'>Photos</p>
                      <p className='text-sm text-grey-700'>
                        {preview.newPlaceData.photoCount} available
                      </p>
                    </div>
                  )}
                </div>

                <div className='flex space-x-2'>
                  <Button
                    label='Confirm Update'
                    icon={mdiCheck}
                    color='danger'
                    small
                    onClick={() => setShowConfirmation(true)}
                  />
                  <Button
                    label='Cancel'
                    color='secondary'
                    small
                    onClick={handleCancelEdit}
                  />
                </div>
              </div>
            )}

            {/* Final Confirmation */}
            {isPlatformAdmin && showConfirmation && (
              <div className='border-t border-grey-200 pt-4 mt-4 space-y-4'>
                <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
                  <div className='flex items-start space-x-3'>
                    <Icon
                      path={mdiAlertCircle}
                      size={24}
                      className='text-red-600 flex-shrink-0'
                    />
                    <div>
                      <h4 className='font-medium text-red-800'>
                        Confirm Place ID Update
                      </h4>
                      <p className='text-sm text-red-700 mt-1'>
                        This will permanently change the map provider reference
                        for this business. After updating, you should sync the
                        business data to apply changes from the new place.
                      </p>
                      <div className='mt-3 text-sm'>
                        <p className='text-grey-600'>
                          <strong>Current:</strong> {mapRefInfo.externalId}
                        </p>
                        <p className='text-grey-600'>
                          <strong>New:</strong> {newPlaceId}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='flex space-x-2'>
                  <Button
                    label='Yes, Update Place ID'
                    icon={mdiCheck}
                    color='danger'
                    small
                    onClick={handleConfirmUpdate}
                    loading={updateMutation.isPending}
                    disabled={updateMutation.isPending}
                  />
                  <Button
                    label='Cancel'
                    color='secondary'
                    small
                    onClick={handleCancelEdit}
                    disabled={updateMutation.isPending}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
            <div className='flex items-start space-x-3'>
              <Icon
                path={mdiAlertCircle}
                size={24}
                className='text-yellow-600 flex-shrink-0'
              />
              <div>
                <h4 className='font-medium text-yellow-800'>
                  No Map Provider Reference
                </h4>
                <p className='text-sm text-yellow-700 mt-1'>
                  This business does not have a linked map provider reference.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <hr className='border-grey-200' />

      {/* Sync Section */}
      <div className='space-y-4'>
        <div className='flex items-center space-x-2'>
          <Icon path={mdiRefresh} size={24} className='text-coffice-blue' />
          <h3 className='text-lg font-medium text-grey-900'>
            Sync Business Data
          </h3>
        </div>

        {/* Success state after applying changes */}
        {!comparison && !isLoading && !error && lastAppliedAt && (
          <>
            <div className='bg-green-50 border border-green-200 rounded-lg p-4'>
              <div className='flex items-center space-x-3'>
                <Icon path={mdiCheck} size={24} className='text-green-600' />
                <div>
                  <h4 className='font-medium text-green-800'>
                    Changes Applied Successfully
                  </h4>
                  <p className='text-sm text-green-700'>
                    {appliedChangesCount} change
                    {appliedChangesCount !== 1 ? 's' : ''} applied from{' '}
                    {lastProviderName || 'Map Provider'}
                  </p>
                  <p className='text-xs text-grey-500 mt-1'>
                    Synced: {new Date(lastAppliedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <Button
              label='Check for More Updates'
              icon={mdiRefresh}
              color='secondary'
              onClick={handleCheckForUpdates}
            />
          </>
        )}

        {/* Initial state - show check for updates button */}
        {!comparison && !isLoading && !error && !lastAppliedAt && (
          <div className='bg-grey-50 rounded-lg p-6 text-center'>
            <Icon
              path={mdiRefresh}
              size={48}
              className='text-grey-400 mx-auto mb-4'
            />
            <p className='text-grey-600 mb-4'>
              Check for updates from the map provider to see if any business
              information has changed.
            </p>
            <Button
              label='Check for Updates'
              icon={mdiRefresh}
              color='primary'
              onClick={handleCheckForUpdates}
              disabled={!mapRefInfo}
            />
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className='flex flex-col items-center justify-center space-y-4 py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-coffice-blue' />
            <p className='text-grey-600'>Fetching data from map provider...</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <>
            <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
              <div className='flex items-start space-x-3'>
                <Icon
                  path={mdiAlertCircle}
                  size={24}
                  className='text-yellow-600 flex-shrink-0'
                />
                <div>
                  <h4 className='font-medium text-yellow-800'>
                    Cannot Sync Business
                  </h4>
                  <p className='text-sm text-yellow-700 mt-1'>
                    This business may not have a map provider reference, or the
                    map provider API is not configured.
                  </p>
                </div>
              </div>
            </div>
            <Button
              label='Try Again'
              icon={mdiRefresh}
              color='secondary'
              onClick={handleCheckForUpdates}
            />
          </>
        )}

        {/* Comparison results */}
        {comparison && (
          <>
            <div className='flex items-center justify-between'>
              <p className='text-xs text-grey-500'>Source: {providerName}</p>
              <Button
                label='Check Again'
                icon={mdiRefresh}
                color='secondary'
                small
                onClick={handleCheckForUpdates}
                disabled={isLoading}
              />
            </div>

            {comparison.lastSyncedAt && (
              <p className='text-sm text-grey-500'>
                Last synced:{' '}
                {new Date(comparison.lastSyncedAt).toLocaleString()}
              </p>
            )}

            {/* No changes */}
            {!hasAnyChanges && (
              <div className='bg-green-50 border border-green-200 rounded-lg p-4'>
                <div className='flex items-center space-x-3'>
                  <Icon path={mdiCheck} size={24} className='text-green-600' />
                  <div>
                    <h4 className='font-medium text-green-800'>Up to Date</h4>
                    <p className='text-sm text-green-700'>
                      No changes detected from {providerName}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Changes list */}
            {hasAnyChanges && (
              <>
                <div className='space-y-3'>
                  <p className='text-sm text-grey-600'>
                    Select the changes you want to apply:
                  </p>

                  {comparison.nameChange &&
                    renderChangeItem(
                      'name',
                      'Business Name',
                      comparison.nameChange.old as string | undefined,
                      comparison.nameChange.new as string | undefined,
                    )}

                  {comparison.addressChange &&
                    renderChangeItem(
                      'address',
                      'Address',
                      comparison.addressChange.old as string | undefined,
                      comparison.addressChange.new as string | undefined,
                    )}

                  {comparison.phoneChange &&
                    renderChangeItem(
                      'phone',
                      'Phone Number',
                      comparison.phoneChange.old as string | undefined,
                      comparison.phoneChange.new as string | undefined,
                    )}

                  {comparison.websiteChange &&
                    renderChangeItem(
                      'website',
                      'Website',
                      comparison.websiteChange.old as string | undefined,
                      comparison.websiteChange.new as string | undefined,
                    )}

                  {renderOpeningHoursChange(comparison)}
                  {renderPhotosChange(comparison)}
                </div>

                <div className='pt-4 border-t border-grey-200'>
                  <Button
                    label={`Apply ${selectedChanges.size} Change${selectedChanges.size !== 1 ? 's' : ''}`}
                    icon={mdiCheck}
                    color='primary'
                    onClick={handleApplyChanges}
                    disabled={
                      selectedChanges.size === 0 ||
                      applyRefreshMutation.isPending
                    }
                    loading={applyRefreshMutation.isPending}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
